import { type ReactNode, useState } from "react";
import { Button, Chip, type ChipProps } from "@heroui/react";
import { ChevronDown, Check, X, Clock, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

export type PaymentMethodStatus =
  | "enabled"
  | "disabled"
  | "coming_soon"
  | "setup_required"
  | "locked";

interface PaymentMethodRowProps {
  icon: ReactNode;
  title: string;
  description: string;
  status: PaymentMethodStatus;
  statusLabel?: string;
  value?: ReactNode;
  details?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const statusConfig: Record<
  PaymentMethodStatus,
  {
    icon: ReactNode;
    color: ChipProps["color"];
    labelKey: string;
  }
> = {
  enabled: {
    icon: <Check className="h-3 w-3" />,
    color: "success",
    labelKey: "settings.paymentStatus.enabled",
  },
  disabled: {
    icon: <X className="h-3 w-3" />,
    color: "default",
    labelKey: "settings.paymentStatus.disabled",
  },
  coming_soon: {
    icon: <Clock className="h-3 w-3" />,
    color: "default",
    labelKey: "settings.paymentStatus.comingSoon",
  },
  setup_required: {
    icon: <X className="h-3 w-3" />,
    color: "warning",
    labelKey: "settings.paymentStatus.setupRequired",
  },
  locked: {
    icon: <Lock className="h-3 w-3" />,
    color: "default",
    labelKey: "settings.paymentStatus.locked",
  },
};

export function PaymentMethodRow({
  icon,
  title,
  description,
  status,
  statusLabel,
  value,
  details,
  action,
}: PaymentMethodRowProps) {
  const { t } = useTranslation("app");
  const [isOpen, setIsOpen] = useState(false);
  const config = statusConfig[status];
  const hasDetails = !!details;
  const isUnavailable = status === "coming_soon" || status === "locked";

  const content = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4">
      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
        <div
          className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-default-100 flex items-center justify-center ${isUnavailable ? "opacity-50" : ""}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={`font-medium text-sm sm:text-base ${isUnavailable ? "text-default-500" : ""}`}
            >
              {title}
            </p>
            <Chip
              color={config.color}
              variant="flat"
              size="sm"
              startContent={config.icon}
            >
              {statusLabel ?? t(config.labelKey)}
            </Chip>
          </div>
          <p
            className={`text-xs sm:text-sm text-default-500 ${isUnavailable ? "opacity-70" : ""}`}
          >
            {description}
          </p>
        </div>
      </div>

      <div
        className="flex items-center gap-2 sm:gap-3 ml-auto sm:ml-0 flex-shrink-0"
        onClick={(e) => {
          if (action) e.stopPropagation();
        }}
      >
        {value && (
          <div className="text-xs sm:text-sm font-medium text-right whitespace-nowrap flex items-center gap-2">
            {value}
          </div>
        )}
        {action && (
          <Button
            size="sm"
            variant="flat"
            onPress={action.onClick}
            className="text-xs sm:text-sm"
          >
            {action.label}
          </Button>
        )}
        {hasDetails && (
          <ChevronDown
            className={`h-4 w-4 text-default-500 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
          />
        )}
      </div>
    </div>
  );

  if (!hasDetails) {
    return (
      <div className="border-b border-default-200 last:border-b-0">
        {content}
      </div>
    );
  }

  return (
    <div className="border-b border-default-200 last:border-b-0">
      <div
        className="cursor-pointer hover:bg-default-50 transition-colors"
        onClick={() => setIsOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
        }}
      >
        {content}
      </div>
      {isOpen && (
        <div className="px-6 pb-5 pt-2 border-t border-default-200 bg-default-50">
          {details}
        </div>
      )}
    </div>
  );
}
